import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import uniqWith from 'lodash/uniqWith';
import { IS_ENCRYPTION_ENABLED } from 'src/config';
import { ChatFile } from 'src/content/Chat/types';
import { chatFileComparator } from 'src/content/Chat/utils';
import { getEncryptParams } from 'src/keycloak';
import { StringKeys } from 'src/types/base';
import { RESOURCE_TYPE } from 'src/types/enum';
import { DATE_FORMAT } from 'src/utils/constants';
import {
  decrypt3,
  getChallengeHeader,
  getParam,
  getPayload
} from 'src/utils/encryption';
import {
  getEncryptedFile,
  readFileAsBuffer
} from 'src/utils/files/file_encryption';
import { formatDate } from 'src/utils/utils';

export type Builder = EndpointBuilder<
  BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
  >,
  APITags,
  'api'
>;

export type BuildFormDataParams = {
  file: File;
  fields: Record<string, string>;
};

export type APITags =
  | 'User'
  | 'Files'
  | 'Chats'
  | 'Tags'
  | 'ChatParams'
  | 'Roles'
  | 'UserRoles'
  | 'Permissions'
  | 'IdentityProvider'
  | 'Groups'
  | 'ResourceProvider'
  | 'Favourites'
  | 'DocAnalyst'
  | 'SmartMeet'
  | 'Assistant'
  | 'Icon'
  | 'ResourceSettings'
  | 'Confluence'
  | 'Tenants'
  | 'helpSupport'
  | 'ModelTiersRateLimits'
  | 'WordCreditUsage'
  | 'ServiceClient'
  | 'TenantsLogo'
  | 'Billings'
  | 'Models'
  | 'UserPreferences'
  | 'Notifications'
  | 'NotificationPreferences'
  | 'OrganizationNotificationPreferences';

export const getQueryString = (params: any) => {
  let querystring = ``;
  let updatedParams = removeNullKeys({ ...params });
  Object.keys(updatedParams).forEach((key) => {
    querystring = querystring + `${key}=${getParam(updatedParams[key], true)}&`;
  });

  if (IS_ENCRYPTION_ENABLED) {
    querystring = `${querystring}payload=${btoa(
      getPayload('', getEncryptParams())
    )}`;
  } else {
    querystring = querystring.slice(0, -1);
  }
  return querystring;
};

export const getUnEncryptedQueryString = (params: StringKeys) => {
  let querystring = ``;
  const updatedParams: StringKeys = removeNullKeys({ ...params });
  Object.keys(updatedParams).forEach((key) => {
    querystring = querystring + `${key}=${updatedParams[key] as string}&`;
  });

  querystring = querystring.slice(0, -1);

  return querystring;
};

export const appendFormData = (params: any) => {
  const encryptionParams = getEncryptParams();
  let formData = new FormData();
  Object.keys(params).forEach((key) => {
    let value = params[key];
    let finalVal = value && typeof value === 'object' ? `${[value]}` : value;
    formData.append(key, getParam(finalVal));
  });

  if (IS_ENCRYPTION_ENABLED) {
    formData.append('payload', getPayload('', encryptionParams));
  }

  return formData;
};

export function removeNullKeys(obj) {
  for (let key in obj) {
    if (obj[key] === undefined || obj[key] === '' || obj[key] === null) {
      delete obj[key];
    }
  }
  return obj;
}

export const getHeaders = (url: string, data?: any, ms?: boolean) => {
  if (IS_ENCRYPTION_ENABLED) {
    const encryptionParams = getEncryptParams();
    return {
      headers: {
        Channel: getChallengeHeader(encryptionParams, url, data, ms)
      }
    };
  }
  return {};
};

export const decryptResponse = (response, decryptKey = undefined) => {
  try {
    if (IS_ENCRYPTION_ENABLED) {
      if (response.data) {
        if (typeof response.data === 'object') {
          return response.data;
        }
        let decrypted = decrypt3(
          decryptKey || getEncryptParams()?.k,
          response.data
        );
        return JSON.parse(decrypted);
      } else if (response.error) {
        try {
          return JSON.parse(response.error.data);
        } catch (err) {
          let decrypted = decrypt3(
            decryptKey || getEncryptParams()?.k,
            response?.error?.data
          );
          return { ...JSON.parse(decrypted) };
        }
      } else {
        return undefined;
      }
    }
    return response.data
      ? response.data
      : { ...(response?.error?.data || response) };
  } catch (err) {
    return undefined;
  }
};

export const serializeErrorResponse = (response: {
  code: number;
  msg: string;
  extended: { error_name: string; args: string; msg: string };
  ret_val: any;
}): { error: FetchBaseQueryError } => ({
  error: {
    status: (response?.extended?.error_name || 'CUSTOM_ERROR') as any,
    originalStatus: response?.code,
    data: response?.extended?.args || response?.ret_val,
    error: response?.extended?.msg || response?.msg
  }
});

export const getFormattedChatFilesData = (fileData: any[]): ChatFile[] => {
  return uniqWith(
    fileData?.map((item) => ({
      ...item,
      id: String(item.file_id),
      author: item.author ?? undefined,
      created_on: formatDate(item.created_on, DATE_FORMAT),
      displayName: item.display_name || item.name,
      ...(item.type_code === RESOURCE_TYPE.TAG && {
        id: String(item.tag_id),
        file_id: item.tag_id,
        name: item.tag_name
      })
    })),
    chatFileComparator
  );
};

export const formatMessagesResponse = (response) => {
  let data = response?.data?.data;
  const isCanvasChat = data?.chat_data?.canvas_id || data?.canvas_id;
  if (isCanvasChat) {
    const messagesLength = data?.msgs?.length;
    if (messagesLength > 3) {
      const hasLastRevision =
        data?.msgs[messagesLength - 1]?.canvas_revision_id;
      if (!hasLastRevision) {
        let previousRevision = data?.msgs?.findLast(
          (message) => message?.canvas_revision_id
        );
        data.msgs[messagesLength - 1] = {
          ...data?.msgs[messagesLength - 1],
          canvas_revision_id: previousRevision?.canvas_revision_id,
          canvas_content: previousRevision?.canvas_content
        };
        return { data: { data: data } };
      }
    }
  }
  return response;
};

export const buildEncryptedFileFormData = async ({
  file,
  fields
}: BuildFormDataParams): Promise<FormData> => {
  const formData = new FormData();
  const encryptionParams = getEncryptParams();
  const arrayBufferData: any = await readFileAsBuffer(file);
  const encryptedFile = await getEncryptedFile(
    arrayBufferData,
    file.name,
    file.type
  );

  formData.append('file', encryptedFile);

  Object?.entries(fields)?.forEach(([key, value]) => {
    formData.append(key, getParam(value));
  });

  if (IS_ENCRYPTION_ENABLED) {
    formData.append('payload', getPayload('', encryptionParams));
  }

  return formData;
};
